import {
	DICTIONARIES_IN_API,
	GAME_TYPES,
	PAGES,
	UNSCRAMBLER_MAX_LENGTH,
} from "@/common/consts";
import {
	trackEvent,
	isEmpty,
	alphabetizeTiles,
	sanitizeTiles,
	groupWordsPerTable,
} from "@/common/functions";
import { getBucketId } from "@/common/feature-toggling";
import wordfinder from "@/common/wordfinder";

// Returns true when the current page should be no-indexed based on the query filters or empty results
function shouldNoIndex(params) {
	const allowStarts = [
		"anti",
		"aqua",
		"auto",
		"counter",
		"fore",
		"hyper",
		"inter",
		"kilo",
		"mega",
		"mini",
		"mono",
		"over",
		"ploy",
		"poly",
		"pseudo",
		"quea",
		"semi",
		"super",
		"tele",
		"trans",
		"ultra",
		"under",
		"vice",
	];
	const allowContains = ["bine", "thie"];
	const allowEnds = [
		"able",
		"ance",
		"ence",
		"less",
		"ment",
		"ness",
		"ship",
		"sion",
		"tion",
	];

	// Queries filtering with more than 15 characters
	if (params.word_length && params.word_length > 15) return true;

	if (
		params.starts &&
		params.starts.length > 3 &&
		!allowStarts.includes(params.starts)
	)
		return true;

	if (
		params.contains &&
		params.contains.length > 3 &&
		!allowContains.includes(params.contains)
	)
		return true;

	if (params.contains && params.contains.includes("_")) return true;

	if (
		params.ends &&
		params.ends.length > 3 &&
		!allowEnds.includes(params.ends)
	)
		return true;

	return false;
}

export default {
	/** Track a Google Event. */
	trackEvent: function(context, payload) {
		const {
			eventCategory,
			eventAction,
			eventLabel,
			eventValue,
			eventID,
			nonInteraction,
		} = payload;
		const eventData = {
			eventCategory,
			eventAction,
			eventLabel,
			eventValue,
			eventID,
			nonInteraction,
		};

		const bucketId = getBucketId();
		if (bucketId) {
			eventData.metric1 = bucketId;
		}

		const toggles = context.state?.features?.toggles || [];
		if (toggles) {
			const dimensionStr = `${
				bucketId ? `${bucketId}:` : ""
			}${toggles.join(",")}`;
			eventData.dimension4 = dimensionStr;
		}

		if (
			isFeatureEnabled("DebugMode") &&
			isFeatureEnabled("DebugAnalytics")
		) {
			console.table(eventData);
		}

		// we need eventID only for debugging and search for specific GA events
		delete eventData.eventID;
		trackEvent(eventData);
	},
	removeSavedWord: function(context, payload) {
		return new Promise((resolve) => {
			let removeWordIndex = context.state.savedWords.findIndex(
				(savedWord) => savedWord.word === payload
			);
			context.commit("removeSavedWord", removeWordIndex);
			resolve();
		});
	},
	getWords: function(context, params) {
		if (params.tiles && params.tiles.length > UNSCRAMBLER_MAX_LENGTH) {
			return;
		}

		// All queries are grouped by word length, even if it's only one word length.
		params.group_by = "word_length";

		// isAnagramPage || isAnagramResultsPage because the earlier method isAnagramPage was checking for both lading and results page. Now created two separate getters.
		if (
			context.getters["navigationChecks/isAnagramPage"] ||
			context.getters["navigationChecks/isAnagramResultsPage"]
		) {
			if (params.dictionary !== DICTIONARIES_IN_API.ANAGRAM) {
				params.dictionary = DICTIONARIES_IN_API.ANAGRAM;
				context.commit("setDictionary", DICTIONARIES_IN_API.ANAGRAM);
			}
		}

		if (
			context.getters["navigationChecks/isWordlePage"] ||
			context.getters["navigationChecks/isWordleResultsPage"]
		) {
			params.dictionary = DICTIONARIES_IN_API.WORDLE_ALL;
		}

		if (
			context.getters["navigationChecks/isQuordlePage"] ||
			context.getters["navigationChecks/isQuordleResultsPage"]
		) {
			params.dictionary = DICTIONARIES_IN_API.WORDLE;
		}

		// If this is an additional query, no need to go into loading mode.
		if (params.offset === 0) {
			context.commit("startLoading");
		}

		const isWordList = params.gameType === GAME_TYPES.WORDSLISTS;
		let endPoint = isWordList ? "wordlist" : "unscrambler";

		if (params.tiles) {
			params.tiles = sanitizeTiles(
				params.tiles,
				context.state.navigation.current,
				false,
				true
			);

			params.tiles = alphabetizeTiles(
				params.tiles,
				context.state.navigation.current
			);
		}

		if (isWordList) {
			params.has_definition = "check";
			params.suggest_links = true;
		}

		if (!isWordList) {
			params.dictionary_opt = "DEF";
		}

		const gameType = params.gameType;
		delete params.gameType;

		if (
			![
				DICTIONARIES_IN_API.WORDLE_ALL,
				DICTIONARIES_IN_API.WORDLE,
			].includes(params.dictionary)
		) {
			params.dictionary = context.getters.getCurrentDictionary.value;
		}

		if (gameType === GAME_TYPES.UNSCRAMBLE) {
			params.dictionary_opt = "YDR"; // Report whether words exist in YDR
			let options = context.getters.getAdvancedSearchOptions;
			Object.assign(params, options);

			if (
				(context.state.navigation.current ===
					PAGES.UNSCRAMBLER_RESULTS ||
					context.state.navigation.current ===
						PAGES.SCRABBLE_DICT_RESULTS) &&
				context.getters.countWildcards === 0 &&
				params.dictionary !== DICTIONARIES_IN_API.ALL
			) {
				params.check_exact_match = true;
				params.exclude_original = context.getters.hasScoreField; // we only exclude the original if the word is playable
				params.original_tiles = context.state.query.tiles;
			}
		}

		params.tiles = isEmpty(context.state.query.prefixedTiles)
			? params.tiles
			: context.state.query.prefixedTiles;

		return wordfinder
			.get(`v1/${endPoint}`, { params })
			.then((response) => {
				const { data } = response.data;

				/**
				 * @note
				 * Even single-table queries (e.g. for Wordle)
				 * are grouped by word_length. This means that
				 * data._groups will always be an array, even if
				 * it only has one entry (or zero entries if no
				 * results are available).
				 *
				 * This also holds true for second queries (i.e.
				 * loading more results into a specific table
				 * by clicking See More). The only difference
				 * between a first and second query would be the
				 * value of data._meta.offset. This makes it a lot
				 * easier to standardise how to add entries into the
				 * store.
				 */

				const isNewAnagramSolver =
					context.getters["navigationChecks/isAnagramPage"] ||
					context.getters["navigationChecks/isAnagramResultsPage"];

				const wordGroups = isNewAnagramSolver
					? data._groups.filter(
							(group) => group.word_length !== params.tiles.length
					  )
					: data._groups;
				const groupedResultsData = groupWordsPerTable(wordGroups);

				if (isNewAnagramSolver) {
					context.commit("setAnagrams", {
						data: data._groups.filter(
							(group) => group.word_length === params.tiles.length
						),
					});
				}

				const payload = {
					...params,
					gameType,
					data: groupedResultsData,
					offset: data._meta.offset,
				};

				if (
					typeof window.__PRERENDER_INJECTED !== "undefined" &&
					isWordList &&
					(data._meta.total === 0 || shouldNoIndex(params))
				) {
					// Fire SSR event to request no-index on low value word list pages as per WFW-4257
					window.document.dispatchEvent(
						new CustomEvent("spa-render-noindex")
					);
				}

				if (!params.use_all_wildcards) {
					context.commit("setWords", payload);
				}

				if (data._augment && data._augment._meta.total) {
					// This is a first response for a query with alternative results
					payload.data = groupWordsPerTable(data._augment._groups);
					context.commit(
						"alternativeResults/setAlternativeWords",
						payload
					);
					context.commit(
						"alternativeResults/setAlternativeResultsTiles",
						data._augment._meta.augment_tiles
					);
					context.commit(
						"alternativeResults/setAlternativeResultsWildcardsCount",
						data._augment._meta.augment_wildcards
					);
				} else if (params.use_all_wildcards) {
					// This is a paginated response for a alternative results table
					context.commit(
						"alternativeResults/setAlternativeWords",
						payload
					);
				} else {
					context.commit(
						"alternativeResults/setAlternativeWords",
						null
					);
					context.commit(
						"alternativeResults/setAlternativeResultsTiles",
						""
					);
					context.commit(
						"alternativeResults/setAlternativeResultsWildcardsCount",
						0
					);
				}

				if (!params.use_all_wildcards) {
					context.commit(
						"updateResultsExactMatch",
						data._exact_match
					);
					if (params.offset === 0) {
						context.commit("updateResultsCount", data._meta.total);
						context.commit(
							"updateSuggestedWordlistLinks",
							data._meta?.links
						);
					}
				}

				if (data._meta?.interlinks) {
					context.commit("updateInterlinks", data._meta.interlinks);
				}
			})
			.catch(() => context.commit("errorFlag"))
			.finally(() => {
				context.commit("stopLoading");
			});
	},
	resetWords: function(context) {
		context.commit("setWords", null);
		context.commit("setAnagrams", null);
		context.commit("alternativeResults/setAlternativeWords", null);
	},
};
